/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */

import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, Input, InputGroup, Checkbox, Loader, Form, SelectPicker } from 'rsuite';
import { MdSearch, MdAdd } from 'react-icons/md';
import styled from 'styled-components';
import { getQuestoes } from '../../../services/treinamentos';
import api from '../../../services/api';
import { ResponsiveForm } from '../../../components/ResponsiveForm';

const SearchContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  gap: 16px;
  flex-direction: row;
`;

const QuestionList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  // border: 1px solid #e0e0e0;
  border-radius: 8px;

  .rs-theme-dark & {
    border-color: #444;
  }
`;

const QuestionItem = styled.div`
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  transition: background 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f5f5f5;
  }

  &.selected {
    background: rgba(59, 130, 246, 0.1);
  }

  .rs-theme-dark & {
    border-color: #444;

    &:hover {
      background: #333;
    }
  }
`;

const QuestionContent = styled.div`
  flex: 1;

  .enunciado {
    font-weight: 500;
    margin-bottom: 8px;
    color: #333;

    .rs-theme-dark & {
      color: #fff;
    }
  }

  .alternativas {
    font-size: 13px;
    color: #666;
    margin-left: 16px;

    .rs-theme-dark & {
      color: #aaa;
    }

    .alternativa {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 2px 0;

      &.correta {
        color: #4caf50;
        font-weight: 500;
      }
    }
  }

  .meta {
    font-size: 12px;
    color: #888;
    margin-top: 8px;
  }
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SelectedCount = styled.span`
  font-size: 14px;
  color: #666;

  .rs-theme-dark & {
    color: #aaa;
  }
`;

const QuestionSelector = ({
  open,
  onClose,
  onSelect,
  onCreateNew,
  multiple = false,
  excludeIds = [],
  title = 'Selecionar Questão',
}) => {
  const [questoes, setQuestoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [_total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [categoriasQuestoes, setCategoriasQuestoes] = useState([]);
  const [id_categoria, setId_categoria] = useState();

  // Memoize excludeIds to prevent infinite loops
  const excludeIdsString = JSON.stringify(excludeIds);

  const loadQuestoes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getQuestoes(pagina, busca, id_categoria);
      const excludeIdsParsed = JSON.parse(excludeIdsString);
      const filtered = data.questoes.filter(q => !excludeIdsParsed.includes(q.id));
      setQuestoes(filtered);
      setTotal(data.total);
    } catch (error) {
      console.error('Erro ao carregar questões:', error);
    } finally {
      setLoading(false);
    }
  }, [pagina, busca, excludeIdsString, id_categoria]);

  const buscarCategoriasQuestoes = useCallback(async () => {
      try {
        const resposta = await api.get('categorias-questoes');
        setCategoriasQuestoes(resposta.data.categorias);
      } catch (error) {
        console.error('Erro ao buscar categorias de questões:', error);
      }
    }, []);

  useEffect(() => {
    if (open) {
      loadQuestoes();
      buscarCategoriasQuestoes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pagina, busca, id_categoria]); // loadQuestoes is intentionally excluded to prevent infinite loop

  useEffect(() => {
    if (!open) {
      setSelectedIds([]);
      setBusca('');
      setPagina(1);
    }
  }, [open]);

  const handleSearch = useCallback((value) => {
    setBusca(value);
    setPagina(1);
  }, []);

  const handleSelect = useCallback((questao) => {
    if (multiple) {
      setSelectedIds((prev) => {
        if (prev.includes(questao.id)) {
          return prev.filter(id => id !== questao.id);
        }
        return [...prev, questao.id];
      });
    } else {
      onSelect(questao);
      onClose();
    }
  }, [multiple, onSelect, onClose]);

  const handleConfirm = useCallback(() => {
    if (multiple) {
      const selected = questoes.filter(q => selectedIds.includes(q.id));
      onSelect(selected);
    }
    onClose();
  }, [multiple, selectedIds, questoes, onSelect, onClose]);

  const isSelected = useCallback((id) => {
    return selectedIds.includes(id);
  }, [selectedIds]);

  return (
    <Modal open={open} onClose={onClose} size="md">
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SearchContainer>
          <InputGroup>
            <InputGroup.Addon>
              <MdSearch />
            </InputGroup.Addon>
            <Input
              placeholder="Buscar por enunciado..."
              value={busca}
              onChange={handleSearch}
            />
          </InputGroup>
          <Form fluid>
                    <Form.Group>
                      <ResponsiveForm
                        name="categoria"
                        data={categoriasQuestoes.map((cat) => ({
                          label: cat.nome,
                          value: cat.id,
                        }))}
                        value={id_categoria}
                        onChange={(value) => setId_categoria(value)}
                        placeholder="Selecione a categoria"
                        accepter={SelectPicker}
                      />
                    </Form.Group>
                  </Form>
        </SearchContainer>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Loader size="md" content="Carregando questões..." />
          </div>
        ) : questoes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
            <p>Nenhuma questão encontrada</p>
          </div>
        ) : (
          <QuestionList>
            {questoes.map((questao) => (
              <QuestionItem
                key={questao.id}
                className={isSelected(questao.id) ? 'selected' : ''}
                onClick={() => handleSelect(questao)}
              >
                {multiple && (
                  <Checkbox
                    checked={isSelected(questao.id)}
                    onChange={() => {}}
                  />
                )}
                <QuestionContent>
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>

              <div className="enunciado">{questao.enunciado}</div>
              <div style={{color: '#bbb', width: '200px', textAlign: 'right'}}>Categoria: {questao.categorias_questoes &&questao.categorias_questoes.nome ? questao.categorias_questoes.nome : 'Genérico'}</div>
              </div>
                  <div className="alternativas">
                    {questao.alternativas?.map((alt) => (
                      <div
                        key={alt.id}
                        className={`alternativa ${alt.correta ? 'correta' : ''}`}
                      >
                        {alt.correta ? '✓' : '○'} {alt.texto}
                      </div>
                    ))}
                  </div>
                  <div className="meta">
                    {questao.alternativas?.length || 0} alternativas
                  </div>
                </QuestionContent>
              </QuestionItem>
            ))}
          </QuestionList>
        )}

        {onCreateNew && (
          <div style={{ marginTop: 16 }}>
            <Button appearance="link" onClick={onCreateNew}>
              <MdAdd style={{ marginRight: 4 }} />
              Criar Nova Questão
            </Button>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <FooterActions>
          {multiple && (
            <SelectedCount>
              {selectedIds.length} questão(ões) selecionada(s)
            </SelectedCount>
          )}
          <div>
            <Button onClick={onClose} appearance="subtle">
              Cancelar
            </Button>
            {multiple && (
              <Button
                onClick={handleConfirm}
                appearance="primary"
                disabled={selectedIds.length === 0}
              >
                Adicionar {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
              </Button>
            )}
          </div>
        </FooterActions>
      </Modal.Footer>
    </Modal>
  );
};

export default QuestionSelector;
